import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";

const CLIPS = [
  { video: "https://customer-assets-eiarnc6j.emergentagent.net/job_luster-launch/artifacts/bbred6iu_IMG_0318.MOV", poster: "/gallery/poster_0318.jpg", label: "Mobile hand wash", area: "Ottawa" },
  { video: "https://customer-assets-eiarnc6j.emergentagent.net/job_luster-launch/artifacts/jqsc99l3_BDDFFD04-9868-4CC1-8511-7F9938F7D199.MP4", poster: "/gallery/poster_bdd.jpg", label: "Urus cabin detail", area: "Rockcliffe Park" },
  { video: "https://customer-assets-eiarnc6j.emergentagent.net/job_luster-launch/artifacts/xrjy89ia_IMG_6749.MOV", poster: "/gallery/poster_6749.jpg", label: "Supercar interior", area: "Rockcliffe Park" },
  { video: "https://customer-assets-eiarnc6j.emergentagent.net/job_luster-launch/artifacts/htg5h33h_6FA8378E-5D72-4130-9163-B828B4EBAA5F.MP4", poster: "/gallery/poster_6fa.jpg", label: "Detail in motion", area: "Rockcliffe Park" },
];

function Clip({ clip, index }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      data-testid={`video-clip-${index}`}
      className="group relative aspect-[9/16] overflow-hidden rounded-2xl border border-white/10 bg-black"
    >
      <video
        ref={ref}
        src={clip.video}
        poster={clip.poster}
        muted
        loop
        playsInline
        preload="none"
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-brand-red px-2.5 py-1 text-[0.6rem] font-semibold uppercase tracking-wider text-white">
        <Play className="h-2.5 w-2.5 fill-white" /> Live
      </span>
      <div className="absolute inset-x-0 bottom-0 p-4">
        <p className="font-serif text-base font-bold text-white">{clip.label}</p>
        <p className="text-xs text-white/70">{clip.area}</p>
      </div>
    </motion.div>
  );
}

export default function VideoShowcase() {
  return (
    <section data-testid="video-showcase" className="bg-brand-charcoal">
      <div className="container-rv py-24 lg:py-32">
        <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-xl">
            <p className="overline text-brand-red">Straight from the driveway</p>
            <h2 className="mt-4 font-serif text-4xl font-bold leading-tight text-white sm:text-5xl">
              See the work in motion
            </h2>
            <p className="mt-5 text-brand-silver">
              No stock footage, no filters — just real cars we've transformed across Ottawa. Press play in your head,
              then imagine yours next.
            </p>
          </div>
          <Link to="/gallery" data-testid="video-showcase-cta" className="btn-red">
            See the full gallery <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>

        <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
          {CLIPS.map((clip, i) => (
            <Clip key={clip.video} clip={clip} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
